import React, { useState } from "react";
import * as Icons from "lucide-react";
import { useApp } from "@/context/AppContext";
import { MIRRORS, computeLayerStatuses } from "@/lib/layers";
import { PageHeader, SectionCard, StatusBadge, InfoNote } from "@/components/uikit";

const ACCENT = {
  cyan: "border-cyan-700 text-cyan-300",
  emerald: "border-emerald-700 text-emerald-300",
  sky: "border-sky-700 text-sky-300",
  amber: "border-amber-700 text-amber-300",
};

export default function Ada4wm() {
  const { stats } = useApp();
  const layers = computeLayerStatuses(stats);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const visible = layers.filter((l) => filter === "all" || l.mirror === filter);

  return (
    <div>
      <PageHeader overline="Four-Way Mirror" title="ADA-4WM · 33-Layer Architecture" icon="LayoutGrid"
        subtitle="A 33-layer provenance and mission-assurance architecture organized into four analytical mirrors." />

      <InfoNote tone="info" className="mb-6">
        The <strong>Regulatory Mirror</strong> provides alignment / mapping tools (e.g. NIST AI RMF, zero-trust). These are
        mapping aids only — <strong>not</strong> certifications or approvals.
      </InfoNote>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {MIRRORS.map((m) => (
          <div key={m.id} className={`rounded-lg border bg-[#0D121D] p-4 shadow-lg ${ACCENT[m.accent]}`} data-testid={`mirror-${m.id}`}>
            <div className="mb-2 flex items-center gap-2">
              <Icons.SquareStack className="h-4 w-4" aria-hidden="true" />
              <h3 className="font-heading text-sm font-bold uppercase tracking-wide">{m.name}</h3>
            </div>
            <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">Layers {m.range[0]}–{m.range[1]}</p>
            <p className="text-xs leading-relaxed text-slate-400">{m.summary}</p>
          </div>
        ))}
      </div>

      <SectionCard title="Layer Explorer (1–33)" icon="LayoutGrid" testId="layer-explorer-card"
        right={
          <div className="flex flex-wrap gap-1">
            {[{ id: "all", label: "All" }, ...MIRRORS.map((m) => ({ id: m.id, label: m.name.split(" ")[0] }))].map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
                className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                  filter === f.id ? "border-cyan-500 bg-slate-900 text-cyan-300" : "border-slate-700 text-slate-400 hover:text-slate-200"
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        }>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((l) => {
            const isOpen = open === l.n;
            return (
              <div key={l.n} className="rounded-md border border-slate-800 bg-slate-950/50" data-testid={`layer-${l.n}`}>
                <button onClick={() => setOpen(isOpen ? null : l.n)} aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-2 p-3 text-left" data-testid={`layer-toggle-${l.n}`}>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="flex h-7 w-9 shrink-0 items-center justify-center rounded bg-slate-800 font-mono text-xs font-bold text-cyan-300">L{l.n}</span>
                    <span className="truncate text-sm text-slate-200">{l.name}</span>
                  </span>
                  <StatusBadge status={l.status} />
                </button>
                {isOpen && (
                  <div className="border-t border-slate-800 p-3 text-xs text-slate-400">
                    <p>{l.desc}</p>
                    {l.special && <p className="mt-2 font-mono uppercase tracking-wider text-amber-400">Indicator: {l.special}</p>}
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-600">Mirror: {l.mirror}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}
